// src/UsersManual.js
// This module replaces only the Notion iframe guide. No Firebase writes or AI API calls.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import manual from "./manuals/manualData.json";
import manualScreenshots from "./manuals/manualScreenshots";
import "./UsersManual.css";

const ALLOWED_TAGS = new Set([
  "p", "h2", "h3", "h4", "h5", "h6", "strong", "em", "code", "pre", "ul",
  "ol", "li", "blockquote", "hr", "table", "thead", "tbody", "tr", "th", "td",
  "a", "img", "br", "s", "div", "span", "figure", "figcaption",
]);
const ARTICLE_IDS = new Set(manual.articles.map((a) => a.id));
const PUBLIC_BASE = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const LABELS = {
  ja: {
    support: "STOMATASCOPE / 利用者サポート", language: "表示言語", title: "操作ガイド", subtitle: "目的から探せる、StomataStorageの使い方。",
    search: "マニュアル内を検索", placeholder: "例：CSV、パスワード、輪郭、全件読み込み",
    clear: "検索をクリア", noResults: "一致する記事がありません。別の言葉で検索してください。",
    results: "件の記事", all: "すべての記事", menu: "目次を開く / 閉じる",
    current: "Web操作ガイド", legacy: "旧資料を継承", pending: "確認が必要な資料",
    review: "2026.10.09 ・ 提供ソース確認版", noEnglish: "この資料は日本語の旧原文を表示しています。英語原文は今回のエクスポートに含まれていません。",
    prev: "前の記事", next: "次の記事", contents: "このページの内容", searchHeading: "検索結果",
    sourceNote: "Web部分は提供ソースに基づいて更新。本体・Android部分は旧説明の内容を維持して翻訳しています。画像・旧PDFは原本のままです。",
  },
  en: {
    support: "STOMATASCOPE / USER SUPPORT", language: "Language", title: "User guide", subtitle: "Find the StomataStorage procedure for your task.",
    search: "Search this manual", placeholder: "Example: CSV, password, contour, load all",
    clear: "Clear search", noResults: "No matching articles. Try another search term.",
    results: "articles", all: "All articles", menu: "Open / close contents",
    current: "Web operation guide", legacy: "Preserved reference", pending: "Needs confirmation",
    review: "2026.10.09 · Supplied-source review", noEnglish: "Showing the preserved Japanese original. An English source was not included in this export.",
    prev: "Previous", next: "Next", contents: "On this page", searchHeading: "Search results",
    sourceNote: "Web instructions were updated from supplied source. Device and Android translations retain the legacy instructions. Images and legacy PDFs remain unchanged.",
  },
};

function safeUrl(value) {
  if (typeof value !== "string") return null;
  if (Object.prototype.hasOwnProperty.call(manualScreenshots, value)) return manualScreenshots[value];
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/manual-assets/")) return PUBLIC_BASE + value;
  if (/^#[A-Za-z0-9_-]+$/.test(value)) return value;
  return null;
}

// The content is a safe node tree, not raw HTML. No dangerouslySetInnerHTML is used.
function ManualNode({ node, nodeKey, onGuide }) {
  if (typeof node === "string") return node;
  if (!node || !ALLOWED_TAGS.has(node.tag)) return null;
  const tag = node.tag;
  const attrs = node.attrs || {};
  const children = (node.children || []).map((child, index) => (
    <ManualNode key={`${nodeKey}-${index}`} nodeKey={`${nodeKey}-${index}`} node={child} onGuide={onGuide} />
  ));
  if (tag === "a") {
    if (String(attrs.href || "").startsWith("guide:")) {
      const id = attrs.href.slice(6);
      if (!ARTICLE_IDS.has(id)) return <span>{children}</span>;
      return <a href={`#${id}`} onClick={(event) => { event.preventDefault(); onGuide(id); }}>{children}</a>;
    }
    const href = safeUrl(attrs.href);
    return href ? <a href={href} target="_blank" rel="noopener noreferrer">{children}</a> : <span>{children}</span>;
  }
  if (tag === "img") {
    const src = safeUrl(attrs.src);
    return src ? <img src={src} alt={attrs.alt || ""} width={Number(attrs.width) || undefined} height={Number(attrs.height) || undefined} loading="lazy" decoding="async" /> : null;
  }
  if (tag === "hr" || tag === "br") return React.createElement(tag);
  if (tag === "table") return <div className="ss-guide-table-scroll"><table>{children}</table></div>;
  const props = {};
  if (tag === "figure") props.className = "ss-guide-figure";
  if (tag === "ol" && /^\d+$/.test(String(attrs.start || ""))) props.start = Number(attrs.start);
  return React.createElement(tag, props, children);
}

export default function UserManual() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = (i18n.resolvedLanguage || i18n.language || "ja").toLowerCase().split("-")[0] === "en" ? "en" : "ja";
  const labels = LABELS[lang];
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const articleRef = useRef(null);
  const articleId = location.hash.slice(1);
  const article = manual.articles.find((a) => a.id === articleId) || manual.articles[0];
  const contentLang = article.content[lang]?.length > 0 ? lang : "ja";
  const matched = useMemo(() => {
    const terms = search.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
    return manual.articles.filter((a) => terms.every((term) => a.searchText.toLocaleLowerCase().includes(term)));
  }, [search]);
  const hasQuery = search.trim().length > 0;
  const index = manual.articles.findIndex((a) => a.id === article.id);

  const chooseArticle = (id) => {
    if (!ARTICLE_IDS.has(id)) return;
    setSearch("");
    setMenuOpen(false);
    navigate({ pathname: location.pathname, search: location.search, hash: `#${id}` });
  };

  const changeLanguage = (value) => {
    i18n.changeLanguage(value);
    try { localStorage.setItem("i18nextLng", value); } catch (_) { /* Storage may be unavailable. */ }
  };

  useEffect(() => {
    if (!articleRef.current) return;
    // Keep the surrounding app navigation intact; only move to the current guide article.
    articleRef.current.focus({ preventScroll: true });
    const scrollContainer = articleRef.current.closest(".scrollable-container");
    if (scrollContainer) scrollContainer.scrollTop = 0;
  }, [article.id]);

  const titleOf = (item) => item.title[lang] || item.title.ja;
  const summaryOf = (item) => item.summary[lang] || item.summary.ja;

  return (
    <section className="ss-guide" aria-label={labels.title}>
      <header className="ss-guide-hero">
        <div>
          <span className="ss-guide-eyebrow">{labels.support}</span>
          <h1>{labels.title}</h1>
          <p>{labels.subtitle}</p>
        </div>
        <div className="ss-guide-hero-side">
          <div className="ss-guide-languages" aria-label={labels.language}>
            <button type="button" aria-pressed={lang === "ja"} onClick={() => changeLanguage("ja")}>日本語</button>
            <button type="button" aria-pressed={lang === "en"} onClick={() => changeLanguage("en")}>English</button>
          </div>
          <small>{labels.review}</small>
        </div>
      </header>
      <div className="ss-guide-searchbar">
        <label htmlFor="ss-guide-search">{labels.search}</label>
        <div className="ss-guide-search-input">
          <span aria-hidden="true">⌕</span>
          <input id="ss-guide-search" type="search" value={search} placeholder={labels.placeholder}
            onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Escape") setSearch(""); }} />
          {search && <button type="button" onClick={() => setSearch("")} aria-label={labels.clear}>×</button>}
        </div>
        <span className="ss-guide-result-count" role="status">{hasQuery ? `${matched.length} ${labels.results}` : `${manual.articles.length} ${labels.results}`}</span>
      </div>
      <button className="ss-guide-mobile-menu" type="button" aria-expanded={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>{labels.menu}</button>
      <div className="ss-guide-layout">
        <aside className="ss-guide-sidebar" data-open={menuOpen}>
          <nav aria-label={labels.all}>
            {manual.categories.map((category) => {
              const list = manual.articles.filter((a) => a.category === category.id);
              return <div className="ss-guide-nav-group" key={category.id}>
                <h2>{category.title[lang] || category.title.ja}<span>{list.length}</span></h2>
                {list.map((a) => <a key={a.id} href={`#${a.id}`} aria-current={a.id === article.id && !hasQuery ? "page" : undefined}
                  onClick={(event) => { event.preventDefault(); chooseArticle(a.id); }}>{titleOf(a)}</a>)}
              </div>;
            })}
          </nav>
        </aside>
        <main className="ss-guide-main">
          {hasQuery ? <section className="ss-guide-results" aria-label={labels.searchHeading}>
            <h2>{labels.searchHeading} <span>{matched.length}</span></h2>
            {!matched.length && <p>{labels.noResults}</p>}
            {matched.map((a) => <button key={a.id} type="button" className="ss-guide-result-card" onClick={() => chooseArticle(a.id)}>
              <span className="ss-guide-result-category">{a.category === "web" ? labels.current : labels.legacy}</span>
              <strong>{titleOf(a)}</strong><span>{summaryOf(a)}</span><span className="ss-guide-card-arrow" aria-hidden="true">→</span>
            </button>)}
          </section> : <>
            <article className="ss-guide-article" ref={articleRef} tabIndex={-1} lang={contentLang}>
              <header className="ss-guide-article-header">
                <span className={`ss-guide-badge ss-guide-badge--${article.status}`}>{article.status === "source-checked" ? labels.current : article.status === "needs-confirmation" ? labels.pending : labels.legacy}</span>
                <h2>{titleOf(article)}</h2>
                <p>{summaryOf(article)}</p>
              </header>
              {contentLang !== lang && <div className="ss-guide-language-note" role="note">{labels.noEnglish}</div>}
              <div className="ss-guide-body">
                {article.content[contentLang].map((node, idx) => <ManualNode key={`${article.id}-${contentLang}-${idx}`} nodeKey={`${article.id}-${idx}`} node={node} onGuide={chooseArticle} />)}
              </div>
            </article>
            <div className="ss-guide-page-nav">
              {index > 0 ? <button type="button" onClick={() => chooseArticle(manual.articles[index - 1].id)}><small>← {labels.prev}</small><span>{titleOf(manual.articles[index - 1])}</span></button> : <span />}
              {index < manual.articles.length - 1 && <button type="button" onClick={() => chooseArticle(manual.articles[index + 1].id)}><small>{labels.next} →</small><span>{titleOf(manual.articles[index + 1])}</span></button>}
            </div>
          </>}
        </main>
      </div>
      <footer className="ss-guide-footer"><span>StomataScope / Happy Quality</span><small>{labels.sourceNote}</small></footer>
    </section>
  );
}
